import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Trash2 } from "lucide-react";
import { Button, CurrencyInput, DatePicker, Dialog, Field, FieldDescription, FieldError, FieldLabel, Input, Select } from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import {
  useCancelTitle,
  useCreateEntries,
  useCreateTitles,
  useRenegotiate,
  useUpdateEntry,
  useUpdateTitle,
  useVoidEntry,
} from "@/hooks/useFinancial";
import { parseMoney } from "@/lib/financial/calc";
import {
  PAYMENT_METHODS,
  type FinancialAccount,
  type FinancialCategory,
  type FinancialEntry,
  type FinancialTitle,
  type PaymentMethod,
} from "@/lib/financial/types";
import { useFinancialFormat } from "./useFinancialFormat";

export type FormMode =
  | { type: "expense" }
  | { type: "charge" }
  | { type: "entry"; cashSessionId?: string | null; date?: string }
  | { type: "editTitle"; title: FinancialTitle }
  | { type: "editEntry"; entry: FinancialEntry }
  | { type: "reneg"; name: string; titles: FinancialTitle[] };

interface Props {
  mode: FormMode | null;
  onClose: () => void;
  accounts: FinancialAccount[];
  categories: FinancialCategory[];
  today: string;
  onCreatedTitle?: (kind: "receivable" | "payable") => void;
}

type Values = Record<string, string>;

// o Select (Radix) não aceita item com valor vazio
const NO_METHOD = "none";
const REPEAT_OPTIONS = ["1", "3", "6", "12"];
const INSTALLMENTS = ["2", "3", "4", "6", "10", "12"];

function initialValues(mode: FormMode, accounts: FinancialAccount[], categories: FinancialCategory[], today: string, fmt: ReturnType<typeof useFinancialFormat>): Values {
  const firstCat = (dir: "in" | "out") => categories.find((c) => c.direction === dir && c.dre_group !== "loan" && c.dre_group !== "capex")?.id ?? "";
  // "Do que se trata" já nasce com o nome da categoria; a pessoa pode reescrever
  const catLabel = (id: string) => (id ? fmt.category(categories.find((c) => c.id === id)) : "");
  switch (mode.type) {
    case "expense":
      return { name: "", detail: "", about: catLabel(firstCat("out")), category: firstCat("out"), due: today, amount: "", method: "boleto", repeat: "1" };
    case "charge":
      return { name: "", detail: "", about: catLabel(firstCat("in")), category: firstCat("in"), due: today, amount: "", method: "pix", repeat: "1" };
    case "entry":
      return {
        about: catLabel(firstCat("out")),
        direction: "out",
        amount: "",
        category: firstCat("out"),
        date: mode.date ?? today,
        account: (mode.cashSessionId ? accounts.find((a) => a.kind === "cash") : accounts[0])?.id ?? "",
        method: mode.cashSessionId ? "cash" : NO_METHOD,
      };
    case "editTitle": {
      const tt = mode.title;
      return {
        name: tt.counterparty_name,
        detail: tt.counterparty_detail ?? "",
        about: tt.description,
        category: tt.category_id ?? "",
        due: tt.due_date,
        amount: String(Math.abs(Number(tt.amount))),
        method: tt.payment_method,
        repeat: "1",
      };
    }
    case "editEntry": {
      const e = mode.entry;
      return {
        about: e.description,
        direction: Number(e.amount) < 0 ? "out" : "in",
        amount: String(Math.abs(Number(e.amount))),
        category: e.category_id ?? "",
        date: e.competence_date,
        account: e.account_id,
        method: e.payment_method ?? NO_METHOD,
      };
    }
    case "reneg": {
      const total = mode.titles.reduce((a, x) => a + Number(x.amount), 0);
      const d = new Date(`${today}T12:00:00`);
      d.setDate(d.getDate() + 10);
      const first = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      return { name: mode.name, amount: fmt.moneyInput(total), installments: "3", due: first, method: "boleto" };
    }
  }
}

export function FinancialFormDialog({ mode, onClose, accounts, categories, today, onCreatedTitle }: Props) {
  const { t } = useTranslation("financial");
  const fmt = useFinancialFormat();
  const [values, setValues] = useState<Values>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createTitles = useCreateTitles();
  const updateTitle = useUpdateTitle();
  const cancelTitle = useCancelTitle();
  const createEntries = useCreateEntries();
  const updateEntry = useUpdateEntry();
  const voidEntry = useVoidEntry();
  const renegotiate = useRenegotiate();

  useEffect(() => {
    if (mode) {
      setValues(initialValues(mode, accounts, categories, today, fmt));
      setErrors({});
    }
    // o formulário só reinicia quando o modo muda
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const set = (k: string) => (v: string) =>
    setValues((p) => {
      const next = { ...p, [k]: v };
      // trocar entrada/saída leva a categoria para uma do mesmo sentido
      if (k === "direction") {
        const current = categories.find((c) => c.id === p.category);
        if (!current || current.direction !== v) {
          next.category = categories.find((c) => c.direction === v && c.is_active && c.dre_group !== "loan" && c.dre_group !== "capex")?.id ?? "";
        }
      }
      // enquanto a pessoa não escreveu nada próprio, "Do que se trata" acompanha a categoria
      if (next.category !== p.category) {
        const label = (id: string) => (id ? fmt.category(categories.find((c) => c.id === id)) : "");
        if (!p.about?.trim() || p.about === label(p.category)) next.about = label(next.category);
      }
      return next;
    });

  const isTitleForm = mode && (mode.type === "expense" || mode.type === "charge" || mode.type === "editTitle");
  const isReceivable = !!mode && (mode.type === "charge" || (mode.type === "editTitle" && mode.title.kind === "receivable"));
  const isEntryForm = mode && (mode.type === "entry" || mode.type === "editEntry");

  const catOptions = useMemo(() => {
    const dir = isTitleForm ? (isReceivable ? "in" : "out") : values.direction === "in" ? "in" : "out";
    return categories
      .filter((c) => c.is_active && (isTitleForm ? c.direction === dir : true))
      .sort((a, b) => (a.direction === dir ? 0 : 1) - (b.direction === dir ? 0 : 1) || a.sort_order - b.sort_order)
      .map((c) => ({ value: c.id, label: fmt.category(c) }));
  }, [categories, fmt, isTitleForm, isReceivable, values.direction]);

  const methodOptions = PAYMENT_METHODS.map((m) => ({ value: m, label: t(`methods.${m}`) }));
  const busy = [createTitles, updateTitle, cancelTitle, createEntries, updateEntry, voidEntry, renegotiate].some((m) => m.isPending);

  if (!mode) return null;

  const required = z.string().trim().min(1, t("form.required"));
  const amountSchema = z.string().refine((v) => parseMoney(v) > 0, t("form.amountInvalid"));
  const schema = z.object(
    isTitleForm
      ? { name: required, due: required, amount: amountSchema }
      : isEntryForm
        ? { about: required, date: required, amount: amountSchema, account: required }
        : { due: required, amount: amountSchema },
  );

  const fail = () => toast({ title: t("toasts.error"), variant: "destructive" });

  const submit = async () => {
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    const amount = parseMoney(values.amount);
    try {
      if (mode.type === "expense" || mode.type === "charge") {
        const kind = mode.type === "charge" ? "receivable" : "payable";
        await createTitles.mutateAsync({
          repeatMonths: Number(values.repeat) || 1,
          input: {
            kind,
            counterparty_name: values.name.trim(),
            counterparty_detail: values.detail.trim() || null,
            description: values.about.trim() || fmt.category(categories.find((c) => c.id === values.category)),
            category_id: values.category || null,
            payment_method: values.method as PaymentMethod,
            due_date: values.due,
            amount,
          },
        });
        toast({ title: t(kind === "receivable" ? "toasts.chargeCreated" : "toasts.expenseCreated", { value: fmt.brl(amount), date: fmt.dnum(values.due) }) });
        onCreatedTitle?.(kind);
      } else if (mode.type === "editTitle") {
        await updateTitle.mutateAsync({
          id: mode.title.id,
          patch: {
            counterparty_name: values.name.trim(),
            counterparty_detail: values.detail.trim() || null,
            description: values.about.trim(),
            category_id: values.category || null,
            payment_method: values.method as PaymentMethod,
            due_date: values.due,
            amount,
          },
        });
        toast({ title: t("toasts.saved") });
      } else if (mode.type === "entry" || mode.type === "editEntry") {
        const signed = values.direction === "out" ? -amount : amount;
        const payload = {
          account_id: values.account,
          category_id: values.category || null,
          amount: signed,
          description: values.about.trim(),
          payment_method: (values.method && values.method !== NO_METHOD ? values.method : null) as PaymentMethod | null,
          competence_date: values.date,
        };
        if (mode.type === "entry") {
          const isToday = values.date === today;
          await createEntries.mutateAsync([
            {
              ...payload,
              occurred_at: isToday ? new Date().toISOString() : new Date(`${values.date}T12:00:00`).toISOString(),
              origin: mode.cashSessionId ? "cashbox" : "manual",
              cash_session_id: mode.cashSessionId ?? null,
            },
          ]);
          toast({ title: t("toasts.entryCreated") });
        } else {
          const prevDate = mode.entry.competence_date;
          await updateEntry.mutateAsync({
            id: mode.entry.id,
            patch: {
              ...payload,
              ...(prevDate !== values.date ? { occurred_at: new Date(`${values.date}T12:00:00`).toISOString() } : {}),
            },
          });
          toast({ title: t("toasts.saved") });
        }
      } else if (mode.type === "reneg") {
        const n = Number(values.installments) || 1;
        await renegotiate.mutateAsync({
          titleIds: mode.titles.map((x) => x.id),
          installments: n,
          firstDue: values.due,
          paymentMethod: values.method as PaymentMethod,
          description: t("form.renegDescription"),
        });
        toast({ title: t("toasts.renegotiated", { count: n, name: mode.name, date: fmt.dnum(values.due) }) });
      }
      onClose();
    } catch {
      fail();
    }
  };

  const remove = async () => {
    try {
      if (mode.type === "editTitle") await cancelTitle.mutateAsync(mode.title.id);
      if (mode.type === "editEntry") await voidEntry.mutateAsync(mode.entry.id);
      toast({ title: t("toasts.deleted") });
      onClose();
    } catch {
      fail();
    }
  };

  const titleKey =
    mode.type === "editTitle" ? (isReceivable ? "editRec" : "editPay") : mode.type === "reneg" ? "reneg" : mode.type;
  const ctaKey = mode.type.startsWith("edit") ? "save" : mode.type;

  const text = (k: string, label: string, opts: { span?: boolean; ph?: string; hint?: string } = {}) => (
    <Field invalid={!!errors[k]} className={opts.span ? "sm:col-span-2" : undefined}>
      <FieldLabel>{label}</FieldLabel>
      <Input value={values[k] ?? ""} placeholder={opts.ph} onChange={(e) => set(k)(e.target.value)} />
      {opts.hint ? <FieldDescription>{opts.hint}</FieldDescription> : null}
      {errors[k] ? <FieldError>{errors[k]}</FieldError> : null}
    </Field>
  );
  const money = (k: string, label: string) => (
    <Field invalid={!!errors[k]}>
      <FieldLabel>{label}</FieldLabel>
      <CurrencyInput
        placeholder="0,00"
        invalid={!!errors[k]}
        value={parseMoney(values[k] ?? "") || undefined}
        onValueChange={(v) => set(k)(v == null ? "" : String(v))}
      />
      {errors[k] ? <FieldError>{errors[k]}</FieldError> : null}
    </Field>
  );
  const date = (k: string, label: string, hint?: string) => (
    <div>
      <DatePicker label={label} value={values[k] ?? ""} onDateChange={set(k)} />
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      {errors[k] ? <p role="alert" className="mt-1 text-xs text-destructive">{errors[k]}</p> : null}
    </div>
  );
  const select = (k: string, label: string, options: { value: string; label: string }[], opts: { span?: boolean; hint?: string } = {}) => (
    <div className={opts.span ? "sm:col-span-2" : undefined}>
      <Select label={label} options={options} value={values[k] ?? ""} onValueChange={set(k)} />
      {opts.hint ? <p className="mt-1 text-xs text-muted-foreground">{opts.hint}</p> : null}
    </div>
  );

  const canDelete = mode.type === "editTitle" || mode.type === "editEntry";

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t(`form.titles.${titleKey}`)}
      closeLabel={t("form.cancel")}
      footer={
        <div className="flex w-full flex-wrap items-center gap-2">
          {canDelete ? (
            <Button variant="outline" onClick={remove} disabled={busy} className="border-destructive/30 text-destructive">
              <Trash2 className="mr-1.5 h-4 w-4" aria-hidden />
              {t("form.delete")}
            </Button>
          ) : null}
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose} disabled={busy}>{t("form.cancel")}</Button>
          <Button onClick={submit} loading={busy}>{t(`form.ctas.${ctaKey}`)}</Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 items-start gap-3.5 sm:grid-cols-2">
        {isTitleForm ? (
          <>
            {text("name", isReceivable ? t("form.client") : t("form.payee"), { span: true, ph: isReceivable ? t("form.clientPh") : t("form.payeePh") })}
            {select("category", t("form.category"), catOptions, { span: true })}
            {text("about", t("form.about"), { span: true, ph: isReceivable ? t("form.aboutPhRec") : t("form.aboutPhPay") })}
            {text("detail", isReceivable ? t("form.detailRec") : t("form.detailPay"), { span: true, ph: isReceivable ? t("form.detailPhRec") : t("form.detailPhPay") })}
            {date("due", t("form.due"), isReceivable ? t("form.dueHintRec") : t("form.dueHintPay"))}
            {money("amount", t("form.amount"))}
            {select("method", t("form.method"), methodOptions)}
            {mode.type !== "editTitle"
              ? select(
                  "repeat",
                  t("form.repeat"),
                  REPEAT_OPTIONS.map((r) => ({ value: r, label: r === "1" ? t("form.repeatNone") : t("form.repeatN", { count: Number(r) }) })),
                )
              : null}
          </>
        ) : null}

        {isEntryForm ? (
          <>
            {select("direction", t("form.direction"), [
              { value: "in", label: t("form.in") },
              { value: "out", label: t("form.out") },
            ])}
            {money("amount", t("form.amount"))}
            {select("category", t("form.category"), catOptions, { span: true })}
            {text("about", t("form.about"), { span: true, ph: t("form.aboutPhEntry") })}
            {date("date", t("form.date"))}
            {select("account", t("form.account"), accounts.map((a) => ({ value: a.id, label: fmt.account(a) })), { hint: t("form.accountHint") })}
            {select("method", t("form.method"), [{ value: NO_METHOD, label: t("form.noMethod") }, ...methodOptions], { span: true })}
          </>
        ) : null}

        {mode.type === "reneg" ? (
          <>
            <div className="sm:col-span-2">
              <p className="text-xs font-semibold text-muted-foreground">{t("form.client")}</p>
              <p className="font-display text-base font-bold">{values.name}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground">{t("form.openTotal")}</p>
              <p className="font-display text-base font-bold">R$ {values.amount}</p>
            </div>
            {select("installments", t("form.installments"), INSTALLMENTS.map((n) => ({ value: n, label: n })))}
            {date("due", t("form.firstDue"))}
            {select("method", t("form.method"), methodOptions)}
          </>
        ) : null}
      </div>
    </Dialog>
  );
}
